import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { IonicModule } from '@ionic/angular';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { environment } from '#environment';
import { authenticateByContractNumber } from '@qvs/core/auth';
import { TranslatePipe } from '@ngx-translate/core';
import { Store } from '@ngrx/store';
import { loginRoutePaths } from '@ebanking/features/login/login.route-paths';
import { combineLatest, Observable, Subject } from 'rxjs';
import { selectBeforeMaintenanceMessage, selectIsBeforeMaintenanceOngoing } from '@qvs/core/remoteConfig';
import { InfoBoxComponent } from '~/shared/components/info-box/info-box.component';
import { distinctUntilChanged, map, throttleTime } from 'rxjs/operators';
import { LoginPageHeaderComponent } from '@ebanking/features/login/login-page-header/login-page-header.component';

@Component({
  selector: 'qvs-login-form',
  standalone: true,
  imports: [CommonModule, IonicModule, ReactiveFormsModule, TranslatePipe, InfoBoxComponent, LoginPageHeaderComponent],
  templateUrl: './login-form.component.html',
  styleUrls: ['./login-form.component.scss']
})
export class LoginFormComponent {
  readonly loginRoutePaths = loginRoutePaths;
  readonly maintenanceAnnouncement$: Observable<string | null> = combineLatest([
    this.store.select(selectIsBeforeMaintenanceOngoing),
    this.store.select(selectBeforeMaintenanceMessage)
  ]).pipe(
    map(([isOngoing, message]) => (isOngoing ? (message ?? null) : null)),
    distinctUntilChanged()
  );

  loginForm = this.formBuilder.group({
    contractNumber: ''
  });

  private readonly contractNumberSubmit$ = new Subject<string>();

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly store: Store,
    private readonly router: Router
  ) {
    // throttle: first click fires immediately, rapid repeats are ignored
    this.contractNumberSubmit$
      .pipe(throttleTime(750), takeUntilDestroyed())
      .subscribe((contractNumber) =>
        this.store.dispatch(authenticateByContractNumber({ contractNumber, contractNumberPrefixed: contractNumber }))
      );
  }

  goToRegistration() {
    return this.router.navigate([this.loginRoutePaths.REGISTRATION]);
  }

  sendContractNr(event: Event) {
    event.preventDefault();
    const contractNumber = this.loginForm.value.contractNumber;
    this.contractNumberSubmit$.next(contractNumber);
  }

  isButtonDisabled(): boolean {
    const contractNumber = this.loginForm.value.contractNumber;
    if (contractNumber) {
      return !Object.values(environment.contractNumberPatterns).some((pattern) => new RegExp(pattern).exec(contractNumber));
    }
    return true;
  }
}
